import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createTestStorage, isTestMode } from './test-storage';

type LineItemPacingState = {
  open: boolean;
  lineItemId: string;
};

// Represents an undefined value in the store
const UNDEF = '__Z_UNDEFINED__';

export type State = {
  lineItemPacing: LineItemPacingState | null;
  highlightedGraph: string | null;
  // Signal used to indicate that the url has changed
  // We store ag-grid table state in the URL, but don't hook it up to react
  // state, as we don't want to re-render every time it changes. This value allows
  // components that do want to know when the url changes can listen to it
  urlTick: number;
  draftRevisions: Record<string, boolean>; // map to show which campaigns have a draft revision
};

export type Actions = {
  setHighlightedGraph: (graphId: string | null | undefined) => unknown;
  notifyUrlTick: () => void;
  setDraftRevisions: (campaignId: string, hasRevision: boolean) => unknown;
};

export type StoreState = State & Actions;

export const useStore = create<
  StoreState,
  [['zustand/persist', never], ['zustand/devtools', never], ['zustand/immer', never]]
>(
  persist(
    devtools(
      immer((set, _get) => ({
        draftRevisions: {},
        lineItemPacing: null,
        highlightedGraph: null,
        urlTick: 0,
        notifyUrlTick: () => set(state => void (state.urlTick += 1)),
        setHighlightedGraph: (highlightedGraph: string | null | undefined) =>
          set(state => void (state.highlightedGraph = highlightedGraph || null)),
        setDraftRevisions: (campaignId: string, hasRevision: boolean) =>
          set(state => void (state.draftRevisions[campaignId] = hasRevision))
      }))
    ),
    {
      name: 'media-tool-pending',
      version: 3,
      // Because undefined values are not written out by JSON.parse, we need to add a custom replacer
      storage: createJSONStorage(() => (isTestMode() ? createTestStorage() : localStorage), {
        replacer: (_key, value) => (value === undefined ? UNDEF : value)
      }),
      merge: (persisted, current) => ({ ...current, ...deepRestore(persisted as State) })
    }
  )
);

// Helper function to restore undefined values from the store
// Can't use the reviver option of JSON.parse
function deepRestore(v: any): any {
  if (v === UNDEF) return undefined;
  if (Array.isArray(v)) return v.map(deepRestore);
  if (v && typeof v === 'object')
    return Object.fromEntries(Object.entries(v).map(([k, val]) => [k, deepRestore(val)]));
  return v;
}
